<template>
  <div>
    <div v-if="isHotel" class="mb-24">
      <label class="label text-sm mb-8">
        <span>{{ $t('tourPricing.nights') }}</span>
      </label>
      <div class="d-flex align-items-center">
        <v-button 
          class="p-0" 
          size="sm" 
          variant="icon"
          @click="decreaseNights"
          :disabled="nights <= 1"
        >
          <v-icon icon="remove" size="sm" />
        </v-button>
        <div class="mx-8 text-sm">{{ nights }}</div>
        <v-button 
          class="p-0" 
          size="sm" 
          variant="icon"
          @click="increaseNights"
          :disabled="nights >= remainingDays"
        >
          <v-icon icon="circle-plus" size="sm" />
        </v-button>
      </div>
    </div>
    <v-form-input id="activity-day-item-resource-supplier-observation" v-model="supplierObservation"
      :label="$t('general.shared.supplier_observations')" input-type="textarea" :rows="5" class="mb-24" />
    <div class="d-flex justify-content-end">
      <v-button :disabled="loading" variant="primary" @click="saveObservation">
        {{ $t('general.button.save') }}
        <v-icon icon="arrow-right" size="sm" space="ms-12" />
      </v-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/api';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import { VFormInput } from '@uniqoders/form';
import { useUserStore } from '@/stores/user';

const props = defineProps<{
  agency: string;
  activityId: number;
  activityDayId: number;
  activityDayItemId: number;
  activityDayItemResourceId: number;
  initialObservation?: string;
  nights?: number;
}>();

const emit = defineEmits<{
  (e: 'emitReload'): void;
  (e: 'updateHotelNights', hotel: any, nights: number): void;
}>();

const { t } = useI18n();
const userStore = useUserStore();
const supplierObservation = ref(props.initialObservation || '');
const loading = ref(false);
const nights = ref(props.nights || 1);
const isHotel = computed(() => (userStore.currentTourDayItem?.type || '').toLowerCase() === 'hotel');

const maxNights = computed(() => {
  if (!userStore.currentTemplate?.tourDays) return 1;
  return userStore.currentTemplate.tourDays.length;
});

const remainingDays = computed(() => {
  if (!userStore.currentTemplate?.tourDays) return 1;

  // Resolve the current day order robustly
  let currentDayOrder = (userStore.currentTourDayItem?.tourDay as any)?.order;
  if (!currentDayOrder) {
    const currentActivityDayId = (userStore.currentTourDayItem as any)?.activityDayId || (userStore.currentTourDayItem as any)?.tourDayId;
    const matchedDay: any = (userStore.currentTemplate as any).tourDays.find((d: any) => d.id === currentActivityDayId);
    currentDayOrder = matchedDay?.order || 1;
  }

  const totalDays = userStore.currentTemplate.tourDays.length;
  // Nights limit should be total days - 1 from the start day
  const remaining = Math.max(1, totalDays - Number(currentDayOrder));
  console.log('Remaining days calculation:', {
    currentDayOrder,
    totalDays,
    remaining,
    currentTourDayItem: userStore.currentTourDayItem,
    currentTemplate: userStore.currentTemplate
  });
  return remaining;
});

const increaseNights = () => {
  if (nights.value < remainingDays.value) {
    nights.value += 1;
  }
};

const decreaseNights = () => {
  if (nights.value > 1) {
    nights.value -= 1;
  }
};

const saveObservation = async () => {
  loading.value = true;
  try {
    await api.activityService.updateDayItem(
      props.agency,
      props.activityId,
      props.activityDayId,
      props.activityDayItemId,
      {
        supplier_observations: supplierObservation.value,
        ...(isHotel.value ? { nights: nights.value } : {}),
      }
    );

    // If it's a hotel, emit the update event for group handling
    if (isHotel.value && userStore.currentTourDayItem) {
      console.log('Emitting updateHotelNights event:', {
        hotel: userStore.currentTourDayItem,
        nights: nights.value
      });
      emit('updateHotelNights', userStore.currentTourDayItem, nights.value);
    }

    emit('emitReload');
  } catch (e: any) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};
</script>